/**
 * Subscription plans — must match the backend SubscriptionPlan enum
 * (EasyPlanner.Common.Enums) and the limits in PlanLimits.cs.
 *
 * The limits mirror the backend purely for display and pre-emptive UI (usage
 * meters, disabling an "Add" button at the cap). The backend stays the real
 * gate — it returns HTTP 402 with a code when a limit is hit.
 */

export const SubscriptionPlan = {
  STARTER: 0,
  PROFESSIONAL: 1,
  BUSINESS: 2,
  CUSTOM: 3,
  // Added after the original four, so its value is out of band. Nothing may
  // compare plans with < or >; see the enum's comment in the backend.
  ESSENTIAL: 4,
};

/**
 * The plans a customer can pick for themselves, in the order the grid shows
 * them — which is not the numeric order.
 *
 * Custom is absent on purpose: it is quoted individually, so on the pricing page
 * it is the contact card and nowhere is it a button that starts a subscription.
 * Business is absent for a different reason — see `RETIRED_PLANS`. Both keep
 * their entries in `PlanNameKeys`, `PlanLimits` and `PlanPrice` below, because
 * companies already on them still have to render somewhere (Companies.jsx,
 * PlatformOverview.jsx, the plan panel).
 */
export const PLAN_ORDER = [
  SubscriptionPlan.STARTER,
  SubscriptionPlan.ESSENTIAL,
  SubscriptionPlan.PROFESSIONAL,
];

/**
 * Plans that are no longer sold but still exist on companies.
 *
 * Business came off the public grid when the fourth card became "contact us";
 * anything that counts or lists *companies* has to keep including it, or the
 * companies on it silently drop out of the figures. Anything that offers a
 * choice must not.
 */
export const RETIRED_PLANS = [SubscriptionPlan.BUSINESS];

/** i18n key for each plan's display name. */
export const PlanNameKeys = {
  [SubscriptionPlan.STARTER]: "Pricing.plans.starter.name",
  [SubscriptionPlan.ESSENTIAL]: "Pricing.plans.essential.name",
  [SubscriptionPlan.PROFESSIONAL]: "Pricing.plans.professional.name",
  [SubscriptionPlan.BUSINESS]: "Pricing.plans.business.name",
  [SubscriptionPlan.CUSTOM]: "Pricing.plans.custom.name",
};

/**
 * Display mirror of the backend limits. `null` means unlimited. Keep in sync
 * with PlanLimits.cs.
 *
 * `maxStaff` is 1 on the free tier and null on every paid plan. The free tier
 * sells no extra seats, so its one included specialist is also its ceiling;
 * the paid plans price team size through `PlanSeats` rather than cap it.
 *
 * `includedSmsPerMonth` is the monthly SMS allowance, spent before any bought
 * package. `includedSmsByAgreement` marks the plans whose allowance is set by
 * contract (Business, Custom): the page says so in words, and nothing is
 * included automatically.
 */
export const PlanLimits = {
  [SubscriptionPlan.STARTER]: {
    maxStaff: 1,
    maxBookingsPerMonth: 30,
    hasAnalytics: true,
    showsPlatformBadge: true,
    includedSmsPerMonth: 0,
    includedSmsByAgreement: false,
  },
  [SubscriptionPlan.ESSENTIAL]: {
    maxStaff: null,
    maxBookingsPerMonth: null,
    hasAnalytics: true,
    showsPlatformBadge: false,
    includedSmsPerMonth: 50,
    includedSmsByAgreement: false,
  },
  [SubscriptionPlan.PROFESSIONAL]: {
    maxStaff: null,
    maxBookingsPerMonth: null,
    hasAnalytics: true,
    showsPlatformBadge: false,
    includedSmsPerMonth: 100,
    includedSmsByAgreement: false,
  },
  [SubscriptionPlan.BUSINESS]: {
    maxStaff: null,
    maxBookingsPerMonth: null,
    hasAnalytics: true,
    showsPlatformBadge: false,
    includedSmsPerMonth: 0,
    includedSmsByAgreement: true,
  },
  [SubscriptionPlan.CUSTOM]: {
    maxStaff: null,
    maxBookingsPerMonth: null,
    hasAnalytics: true,
    showsPlatformBadge: false,
    includedSmsPerMonth: 0,
    includedSmsByAgreement: true,
  },
};

/**
 * Display mirror of the backend `PlanSeats`: how many team members the headline
 * price covers, and what one more costs per month.
 *
 * `extraSeatPrice: null` means the plan sells no extra seats at all — the free
 * tier (capped at its one seat instead, see `PlanLimits`), and Custom which has
 * no list price to add to. Everywhere else, every seat past `includedSeats` is
 * chargeable and there is no ceiling: nothing stops a paying business adding
 * people, they simply appear on the bill.
 *
 * Essential charges more per seat than Professional on purpose. It is what keeps Professional
 * worth buying now that no paid plan caps team size — see `PlanSeats.cs`.
 *
 * These are the fallback only. The live numbers ride along with each quote from
 * `GET /api/Pricing` for the same reason the prices do.
 */
export const PlanSeats = {
  [SubscriptionPlan.STARTER]: { includedSeats: 1, extraSeatPrice: null },
  [SubscriptionPlan.ESSENTIAL]: { includedSeats: 1, extraSeatPrice: 13 },
  [SubscriptionPlan.PROFESSIONAL]: { includedSeats: 3, extraSeatPrice: 8 },
  [SubscriptionPlan.BUSINESS]: { includedSeats: 8, extraSeatPrice: 8 },
  [SubscriptionPlan.CUSTOM]: { includedSeats: 0, extraSeatPrice: null },
};

/**
 * Subscription lifecycle — must match the backend SubscriptionStatus enum.
 *
 * ACTIVE is what a subscription is created as and what a confirmed payment
 * returns it to. The rest are set by billing as it learns about failures and
 * cancellations; PAST_DUE and CANCELED are what `LicenceState` reads to flag a
 * company as a problem regardless of what its period end says.
 */
export const SubscriptionStatus = {
  ACTIVE: 0,
  TRIALING: 1,
  PAST_DUE: 2,
  CANCELED: 3,
};

/**
 * Whether a business appears in the public directory — must match the backend
 * `DirectoryListing`.
 *
 * AUTOMATIC means "listed while paying", which is what almost every company is.
 * The other two are a SystemOwner's deliberate override: promoting a partner who
 * pays nothing, or keeping a demo company away from real visitors. A trial does
 * not list anybody — see `DirectoryVisibility` on the server for why.
 */
export const DirectoryListing = {
  AUTOMATIC: 0,
  ALWAYS_LISTED: 1,
  NEVER_LISTED: 2,
};

/**
 * Where one payment attempt stands — must match the backend `PaymentStatus`.
 *
 * PENDING is not a failure. A customer who has just paid lands back here before
 * NETOPIA's notification has reached our server, so the return page shows
 * "pending" for a few seconds on a perfectly successful payment and has to say
 * so rather than reading as a problem.
 */
/** What a payment buys. Mirrors the backend `PaymentPurpose`. */
export const PaymentPurpose = {
  SUBSCRIPTION: 0,
  SMS_PACKAGE: 1,
};

export const PaymentStatus = {
  PENDING: 0,
  PAID: 1,
  FAILED: 2,
  CANCELED: 3,
  REFUNDED: 4,
};

/**
 * Monthly price in EUR, before VAT — the fallback only.
 *
 * The real prices come from `GET /api/Pricing`, because a SystemOwner can
 * change them and run promotions against them; these are what the page shows
 * when that call has not answered yet or has failed. Keeping them means a
 * network blip renders the prices the site shipped with rather than an empty
 * card, and they are the same numbers the backend falls back to
 * (`PlanPricing.FallbackPriceCents`) so the two never disagree.
 *
 * Custom is quoted, so it has no price here and never gains one.
 */
export const PlanPrice = {
  [SubscriptionPlan.STARTER]: 0,
  [SubscriptionPlan.ESSENTIAL]: 13,
  [SubscriptionPlan.PROFESSIONAL]: 29,
  [SubscriptionPlan.BUSINESS]: 69,
  [SubscriptionPlan.CUSTOM]: null,
};

/** The currency symbol for an ISO code. Prices are listed in EUR and charged in RON. */
export const currencySymbol = (code) =>
  ({ EUR: "€", RON: "lei", USD: "$" })[code] || code || "€";

/**
 * Whether a currency's symbol goes after the number rather than before it.
 *
 * "lei" is a word, not a sign: Romanian writes "152 lei", never "lei152". The
 * euro and dollar signs lead. This exists because the price is composed in nine
 * places, and every one of them used to hard-code the symbol in front — which
 * was invisible while the only currency shown was EUR, and would be wrong in
 * all nine the moment a lei figure reached any of them. The checkout step is
 * where that now happens.
 *
 * The default must match `currencySymbol`'s: given no currency, both assume the
 * list currency, or `formatMoney(8)` renders "8 €".
 */
export const currencyTrails = (code) => (code || "EUR") === "RON";

/**
 * A price as a person reads it: no decimals on a round number, two when there
 * are any. 19 stays "19"; half of it is "9.50", not "9.5" — a trailing single
 * decimal on a price reads as a truncation.
 */
export const formatPrice = (value) => {
  if (value == null) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

/**
 * A price with its currency, written the way the currency is written. The one
 * way to put a number and a symbol together — see {@link currencyTrails}.
 */
export const formatMoney = (value, code) => {
  if (value == null) return "";
  const amount = formatPrice(value);
  const symbol = currencySymbol(code);
  return currencyTrails(code) ? `${amount} ${symbol}` : `${symbol}${amount}`;
};

/**
 * Months free when paying for a year — the fallback only, mirroring
 * `PlanPricing.AnnualFreeMonths`. The live number arrives as
 * `annualFreeMonths` on `GET /api/Pricing` and on the subscription payload.
 */
export const ANNUAL_FREE_MONTHS = 2;

/** The two billing periods a payment can buy, in months. */
export const BillingPeriod = {
  MONTHLY: 1,
  ANNUAL: 12,
};

/**
 * A monthly price as a year paid up front — the fallback used before the
 * server's own figure arrives. Rounded to the cent so 12.08 × 10 prints as
 * "120.80" and not as the float it would otherwise be.
 */
const annualOf = (monthly, freeMonths = ANNUAL_FREE_MONTHS) =>
  monthly == null ? null : Math.round(monthly * (12 - freeMonths) * 100) / 100;

/**
 * Turns the API's price list into a lookup by plan.
 *
 * Falls back to `PlanPrice` for anything missing, so a caller can render before
 * the request lands without special-casing the empty state everywhere.
 */
export const pricingByPlan = (pricing) => {
  const byPlan = {};

  // Every plan, not just the four in PLAN_ORDER: Custom is not a card in the
  // grid but a company already on it still has a plan panel to render.
  for (const plan of Object.values(SubscriptionPlan)) {
    const price = PlanPrice[plan] ?? null;
    const seats = PlanSeats[plan] || { includedSeats: 1, extraSeatPrice: null };
    byPlan[plan] = {
      plan,
      listPrice: price,
      price,
      discountPercent: 0,
      currency: "EUR",
      promotionName: null,
      promotionEndsAt: null,
      includedSeats: seats.includedSeats,
      extraSeatPrice: seats.extraSeatPrice,
      extraSeatListPrice: seats.extraSeatPrice,
      maxSeats: PlanLimits[plan]?.maxStaff ?? null,
      annualPrice: annualOf(price),
      annualListPrice: price == null ? null : price * 12,
      annualExtraSeatPrice: annualOf(seats.extraSeatPrice),
    };
  }

  for (const quote of pricing?.plans || []) {
    const seats = PlanSeats[quote.plan] || {
      includedSeats: 1,
      extraSeatPrice: null,
    };
    byPlan[quote.plan] = {
      plan: quote.plan,
      listPrice: quote.listPrice ?? null,
      price: quote.price ?? null,
      discountPercent: quote.discountPercent || 0,
      currency: quote.currency || "EUR",
      promotionName: quote.promotionName || null,
      promotionEndsAt: quote.promotionEndsAt || null,
      // Seat terms come from the server alongside the price, so the card, the
      // wizard tile and the plan panel cannot show three different "+€8 / seat".
      // `?? ` rather than `||`: zero included seats is a real answer.
      includedSeats: quote.includedSeats ?? seats.includedSeats,
      // Already discounted by the plan's promotion — the percentage is off the
      // whole bill, seats included. The list figure is kept for crossing out.
      extraSeatPrice: quote.extraSeatPrice ?? seats.extraSeatPrice,
      extraSeatListPrice:
        quote.extraSeatListPrice ?? quote.extraSeatPrice ?? seats.extraSeatPrice,
      maxSeats: quote.maxSeats ?? PlanLimits[quote.plan]?.maxStaff ?? null,
      // A year paid up front, from the server so the card shows the exact figure
      // the checkout will charge.
      annualPrice: quote.annualPrice ?? annualOf(quote.price ?? null),
      annualListPrice:
        quote.annualListPrice ??
        (quote.listPrice == null ? null : quote.listPrice * 12),
      annualExtraSeatPrice:
        quote.annualExtraSeatPrice ?? annualOf(quote.extraSeatPrice ?? null),
    };
  }

  return byPlan;
};

/**
 * Whether the seat price carries a discount worth striking through — the
 * plan's promotion, applied to each extra team member too.
 */
export const hasSeatDiscount = (quote) =>
  !!quote &&
  quote.extraSeatPrice != null &&
  quote.extraSeatListPrice != null &&
  quote.extraSeatPrice < quote.extraSeatListPrice;

// The client-side seat arithmetic that used to live here (billableExtraSeats /
// quoteForSeats) went with the pricing page's seat stepper. The card states the
// per-seat price and stops there; what a given team actually owes is computed
// server-side, beside the subscription, and arrives on the usage payload.

/**
 * Whether a quote should show a crossed-out price beside the one being charged.
 *
 * Both halves matter: with no discount there is nothing to cross out, and a
 * plan whose list price is already zero would otherwise strike through "€0" and
 * print "€0" next to it.
 */
export const hasDiscount = (quote) =>
  !!quote &&
  quote.discountPercent > 0 &&
  quote.listPrice != null &&
  quote.listPrice > 0 &&
  quote.price != null &&
  quote.price < quote.listPrice;

/**
 * Whole hours until a promotion ends, or null when it is not ending soon
 * enough to be worth saying.
 *
 * Only surfaced under 48 hours: "ends in 27 days" is not urgency, it is
 * clutter, and the countdown exists for the 24-hour sale this was built for.
 */
export const promotionHoursLeft = (endsAt, now = Date.now()) => {
  if (!endsAt) return null;
  const remaining = new Date(endsAt).getTime() - now;
  if (Number.isNaN(remaining) || remaining <= 0) return null;
  const hours = Math.ceil(remaining / 3600000);
  return hours <= 48 ? hours : null;
};

/**
 * The zone a promotion's deadline is stated in. The offer is made by a Romanian
 * company under Romanian terms, and the SystemOwner who set the end entered it
 * in Romanian time — so a visitor abroad is told the same deadline, not a
 * shifted one that disagrees with what support will tell them.
 */
export const PROMOTION_TIME_ZONE = "Europe/Bucharest";

/**
 * The last moment a promotion covers, as a date and a time a person reads — or
 * null when there is no end to state.
 *
 * An end set to midnight means the whole previous day is included, so it is
 * stated as 23:59 that day: "valid until 1 November, 00:00" reads as if the 1st
 * were part of the offer.
 */
export const promotionDeadline = (endsAt, locale = "ro") => {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;

  const time = (d, loc = locale) =>
    d.toLocaleTimeString(loc, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: PROMOTION_TIME_ZONE,
    });

  // Checked in a fixed locale, so the comparison does not depend on how the
  // visitor's language writes a clock.
  const last = time(end, "en-GB") === "00:00" ? new Date(end.getTime() - 60000) : end;

  return {
    date: last.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: PROMOTION_TIME_ZONE,
    }),
    time: time(last),
  };
};

/** Machine-readable reason codes the backend returns with HTTP 402. */
export const PlanLimitCode = {
  STAFF: "PLAN_LIMIT_STAFF",
  BOOKINGS: "PLAN_LIMIT_BOOKINGS",
  ANALYTICS: "PLAN_LIMIT_ANALYTICS",
};

/** localStorage key used to carry a plan chosen on the Pricing page into signup. */
export const SELECTED_PLAN_KEY = "selectedPlan";

/**
 * True when an axios error is a plan-limit rejection (HTTP 402). Optionally
 * narrow to a specific code.
 */
export const isPlanLimitError = (error, code = null) => {
  if (error?.response?.status !== 402) return false;
  return code == null || error.response?.data?.code === code;
};

/** The upgrade message the backend sent with a 402, if any. */
export const planLimitMessage = (error) => error?.response?.data?.message || "";

/**
 * Where one invoice stands — must match the backend `InvoiceStatus`.
 *
 * BLOCKED and FAILED both mean "no document", and they are kept apart for the
 * same reason the server keeps them apart: one is the company's to fix (a CUI
 * ANAF does not recognise) and one is ours. Showing a single "error" would
 * send both to support.
 */
export const InvoiceStatus = {
  PENDING: 0,
  ISSUED: 1,
  BLOCKED: 2,
  FAILED: 3,
};

/**
 * Whole days from now until `date`, floored at zero, counted in UTC.
 *
 * UTC on purpose: it is the arithmetic `PlanEntitlement.TrialDaysLeft` does
 * server-side, and the billing page puts a number we computed next to one the
 * server sent. Counting in the browser's own zone instead would make the two
 * disagree by a day for a few hours every evening east of Greenwich, on the
 * same screen.
 */
export const daysUntil = (date, now = Date.now()) => {
  if (!date) return null;

  const end = new Date(date);
  if (Number.isNaN(end.getTime())) return null;

  const utcDay = (d) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((utcDay(end) - utcDay(new Date(now))) / 86400000);

  return days < 0 ? 0 : days;
};

/**
 * What a subscription is doing right now, in the four shapes the billing page
 * has something different to say about.
 */
export const BillingState = {
  /** The free tier. Nothing is owed and no date is coming. */
  FREE: "free",
  /** Paying nothing yet, but on a clock. */
  TRIAL: "trial",
  /** Paid, with a date it runs to. */
  ACTIVE: "active",
  /** The clock ran out. The plan's features are gone until it is paid. */
  LAPSED: "lapsed",
  /** A paid plan with no period recorded — chosen but never paid for. */
  UNKNOWN: "unknown",
};

/**
 * The subscription's clock, reduced to the three things a person asks of a
 * billing page: what state am I in, how long have I got, and what date does
 * that run out on.
 *
 * `endsAt` is both the end of the current period and when the next payment is
 * due, because for this product they are the same instant — there is no grace
 * window, and pretending otherwise would put a date on screen that nothing
 * enforces.
 */
export const billingPeriod = (usage, now = Date.now()) => {
  const nothing = { state: BillingState.UNKNOWN, daysLeft: null, endsAt: null };

  if (!usage) return nothing;

  if (usage.plan === SubscriptionPlan.STARTER) {
    return { state: BillingState.FREE, daysLeft: null, endsAt: null };
  }

  // Checked before the trial, because a lapsed trial is still `trialEndsAt` in
  // the past and would otherwise report as a trial with zero days left — which
  // reads like it is about to end rather than like it already has.
  if (usage.hasLapsed) {
    return {
      state: BillingState.LAPSED,
      daysLeft: 0,
      endsAt: usage.currentPeriodEnd ?? usage.trialEndsAt ?? null,
    };
  }

  if (usage.isOnTrial) {
    return {
      state: BillingState.TRIAL,
      // The server's own count in preference to ours: it is the number the
      // entitlement rules are actually applied against, so it is the one that
      // will be right when they stop.
      daysLeft: usage.trialDaysLeft ?? daysUntil(usage.trialEndsAt, now),
      endsAt: usage.trialEndsAt ?? null,
    };
  }

  if (usage.currentPeriodEnd) {
    return {
      state: BillingState.ACTIVE,
      daysLeft: daysUntil(usage.currentPeriodEnd, now),
      endsAt: usage.currentPeriodEnd,
    };
  }

  return nothing;
};
